<template>
  <div class="form-group" :class="className">
    <label :for="inputId" class="form-label">{{ labelName }}</label>
    <input
      :id="inputId"
      :aria-describedby="inputId"
      :placeholder="placeholder"
      type="text"
      :value="modelValue"
      class="form-control pj-input"
      :class="{ 'is-invalid': errorMessage }"
      @input="change($event.target.value)"
      ref="inputZipCode"
      :maxlength="maxLength"
    />
    <div class="invalid-feedback" v-if="errorMessage">
      {{ errorMessage }}
    </div>
    <div :id="inputId" class="form-text"></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, toRefs, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import {
  isBrazilSaleFlow,
  isIndonesiaSaleFlow,
} from "@/apps/merchant3/helpers/checkoutFlows";

const props = defineProps({
  className: { type: String },
  labelName: { type: String },
  placeholder: { type: String },
  inputId: { type: String },
  required: { type: Boolean, default: false },
  modelValue: { type: String },
  maxLength: { type: Number, default: 11 },
});

const emit = defineEmits(["update:modelValue"]);

const { modelValue } = toRefs(props);
const { t } = useI18n();
const errorMessage = ref("");
const error = ref(false);
const inputValue = ref<unknown>("" as string);

const format = (value: string): string => {
  if (isBrazilSaleFlow()) {
    return applyBrazilFormat(value);
  }
  if (isIndonesiaSaleFlow()) {
    return value.replace(/\D/g, "").slice(0, 5);
  }

  return value;
};

const applyBrazilFormat = (value: string): string => {
  const digitGroups = value.match(/^(\d{5})(\d{3})$/);

  if (!digitGroups) {
    return value;
  }

  const valueFormated = `${digitGroups[1]}-${digitGroups[2]}`;

  return valueFormated;
};

const validate = (): boolean => {
  if (!inputValue.value && props.required) {
    errorMessage.value = t("SHARED.FIELD_REQUIRED");
    error.value = true;
    return false;
  } else {
    error.value = false;
    errorMessage.value = "";
  }
  emit("update:modelValue", inputValue.value);
  return true;
};

watch(inputValue, (value: unknown) => {
  inputValue.value = value;
  if (value !== "" && value !== "0.00" && value !== undefined) {
    emit("update:modelValue", inputValue.value);
  }
});

watch(
  () => props.modelValue,
  () => {
    inputValue.value = props.modelValue;
  }
);

onMounted(() => (inputValue.value = modelValue.value));

const change = (value: string) => {
  value = format(value);
  inputValue.value = value;
  nextTick(() => {
    validate();
    emit("update:modelValue", inputValue.value);
  });
};
</script>
